import { FC, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { isEmpty } from "lodash";
import __ from "src/utils/Translations";
import { Dropzone } from "@benefit-systems/hr-tools-ui";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import { useImportAbsenceData } from "src/Pages/Company/Absences/api/importAbsenceData.queries";
import ImportLegend from "src/Pages/Company/Absences/AbsenceRecords/AbsenceRecordsImports/ImportLegend";
import ImportErrorList from "src/Pages/Company/Absences/AbsenceRecords/AbsenceRecordsImports/ImportErrorList";
import ImportSkippedRowList from "src/Pages/Company/Absences/AbsenceRecords/AbsenceRecordsImports/ImportSkippedRowList";
import {
  ExportImportStatus,
  ImportType,
} from "src/Pages/Company/Absences/AbsenceRecords/AbsenceRecords.models";

export interface FormValues {
  file: File | null;
}

interface ImportFormProps {
  importType: ImportType;
  maxFileSize: number;
}

const ImportForm: FC<ImportFormProps> = ({ importType, maxFileSize = 3 }) => {
  const history = useHistory();
  const companyId = useCompanyId();
  const [isProcessing, setIsProcessing] = useState(false);
  const {
    importMutation: { mutateAsync, isPending },
    importStatus,
    importErrors,
    skippedRows,
  } = useImportAbsenceData();

  const {
    control,
    handleSubmit,
    formState: { isValid },
    setValue,
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      file: null,
    },
    mode: "onChange",
  });

  const selectedFile = watch("file");

  useEffect(() => {
    if (
      importStatus &&
      (importStatus === ExportImportStatus.SUCCEEDED ||
        importStatus === ExportImportStatus.FAILED)
    ) {
      setIsProcessing(false);
    }
  }, [importStatus]);

  const handleFilesAccepted = (
    acceptedFiles: { file: File; isValid: boolean }[]
  ) => {
    if (acceptedFiles.length > 0 && acceptedFiles[0].isValid) {
      setValue("file", acceptedFiles[0].file, { shouldValidate: true });
    }
  };

  const onSubmit = async (data: FormValues) => {
    if (data.file) {
      setIsProcessing(true);

      await mutateAsync({
        companyId,
        importType,
        file: data.file,
      });
    }
  };

  return (
    <>
      <Paper
        sx={{ padding: "2rem", mt: "2rem" }}
        data-testid="absences-import-form"
      >
        <ImportLegend importType={importType} />

        {isProcessing && (
          <Paper
            sx={{
              position: "fixed",
              top: 80,
              right: 20,
              p: "1.5rem",
              zIndex: 1,
            }}
          >
            <Stack direction="row" sx={{ alignItems: "center", gap: 2 }}>
              <Typography variant="body2">
                {__("Trwa importowanie danych")}
              </Typography>
              <CircularProgress />
            </Stack>
          </Paper>
        )}
        <form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            control={control}
            name="file"
            rules={{ required: true }}
            render={({ field }) => (
              <Dropzone
                onFilesAccepted={handleFilesAccepted}
                allowMultiple={false}
                maxFileSize={maxFileSize}
                acceptTypes={[
                  {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    label: "XLSX",
                  },
                  { type: "text/csv", label: "CSV" },
                  // Firefox reports .csv files as application/vnd.ms-excel instead of text/csv
                  { type: "application/vnd.ms-excel", label: "" },
                ]}
                activeLinkText={__("Dodaj plik z dysku")}
                data-testid="absences-import-form__dropzone"
                translateFunction={__}
                showInlineErrors
                showInvalidFiles
              />
            )}
          />

          {selectedFile && (
            <Box sx={{ mt: 2 }} data-testid="absences-import-form__file">
              <Typography variant="body2">
                {__("Wybrany plik")}: {selectedFile.name}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              mt: 3,
              display: "flex",
              justifyContent: "flex-end",
              gap: "1rem",
            }}
          >
            <Button variant="outlined" onClick={history.goBack}>
              {__("Anuluj")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              size="large"
              disabled={!isValid || !selectedFile}
              loading={isProcessing}
              data-testid="absences-import-form__submit-button"
            >
              {__("Zapisz ustawienia")}
            </Button>
          </Box>
        </form>
      </Paper>
      {importStatus &&
        (importStatus === ExportImportStatus.SUCCEEDED ||
          importStatus === ExportImportStatus.FAILED) && (
          <Paper
            sx={{ padding: "2rem", mt: "2rem" }}
            data-testid="absences-import-status"
          >
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" component="h2" gutterBottom>
                {__("Szczegóły importu:")}
              </Typography>
              <Typography variant="body2">
                {__(
                  "Poniżej znajdziesz informacje dotyczące ostatniego importu."
                )}
              </Typography>
              <Card variant="outlined" sx={{ mt: "1rem" }}>
                <CardContent sx={{ p: "1.5rem" }}>
                  {importStatus === ExportImportStatus.SUCCEEDED && (
                    <>
                      <Stack
                        direction="row"
                        spacing={2}
                        sx={{ alignItems: "center" }}
                      >
                        <CheckCircleIcon color="success" />
                        <Typography variant="subtitle1">
                          {__("Plik został poprawnie zaimportowany")}
                        </Typography>
                      </Stack>
                      {!isEmpty(skippedRows) && (
                        <ImportSkippedRowList skippedRows={skippedRows} />
                      )}
                    </>
                  )}
                  {importErrors && (
                    <ImportErrorList importErrors={importErrors} />
                  )}
                </CardContent>
              </Card>
            </Box>
          </Paper>
        )}
    </>
  );
};

export default ImportForm;
